import React, { useCallback, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';

import Select, {
	type DropdownIndicatorProps,
	type InputActionMeta,
	type MenuProps,
	type OptionProps,
	type OptionType,
	type SelectInstance,
} from '@atlaskit/select';

import {
	type BaseSelectProps,
	type ControlProps,
	MetaActions,
	type SelectOption,
	type SingleOption,
} from '../../../common/types';

import Control from './control';
import DropdownIndicator from './dropdown-indicator';
import Menu from './menu';
import messages from './messages';
import Option from './option';

export const selectComponents: {
	Control: ({ children, innerProps, innerRef }: ControlProps) => React.JSX.Element;
	DropdownIndicator: (props: DropdownIndicatorProps<SingleOption, true>) => React.JSX.Element;
	LoadingIndicator: null;
	Menu: ({ innerRef, innerProps, children }: MenuProps<SingleOption, true>) => React.JSX.Element;
	Option: (props: OptionProps<OptionType, true>) => React.JSX.Element;
} = {
	Control,
	DropdownIndicator,
	LoadingIndicator: null,
	Menu,
	Option,
};

const onIsOptionDisabled = (option: SelectOption) => ('error' in option ? !!option.error : false);

export const LocalBaseSelect: React.ForwardRefExoticComponent<
	Omit<BaseSelectProps, 'ref'> & React.RefAttributes<SelectInstance>
> = React.forwardRef<SelectInstance, BaseSelectProps>(
	({ onFilterOption, placeholder, ...restProps }, ref) => {
		const intl = useIntl();
		const {
			ariaLabel: ariaLabelProp,
			onInputChange: onInputChangeProp,
			inputValue: inputValueProp,
		} = restProps;

		const [inputValue, setInputValue] = useState<string>(inputValueProp || '');

		const onInputChange = useCallback(
			(newValue: string, actionMeta: InputActionMeta) => {
				/**
				 * Do not clear the filter input value when an input is selected or blurred.
				 * NOTE: We are also returning when 'menu-close' event are triggered,
				 * which happens when the input is clicked for some reason, but not when we click outside of the dropdown
				 * */
				if (
					[MetaActions.setValue, MetaActions.inputBlur, MetaActions.menuClose].includes(
						actionMeta.action,
					) &&
					!newValue.length
				) {
					return;
				}

				onInputChangeProp?.(newValue, actionMeta);
				setInputValue(newValue);
			},
			[onInputChangeProp],
		);

		return (
			<Select
				{...(restProps as any)} // Typescript fix: SelectOption is not compatible with SingleOption from Select
				ref={ref}
				label={ariaLabelProp || intl.formatMessage(messages.searchPlaceholder)}
				autoFocus
				backspaceRemovesValue={false}
				blurInputOnSelect={false}
				closeMenuOnSelect={false}
				controlShouldRenderValue={false}
				hideSelectedOptions={false}
				isClearable={false}
				isMulti
				inputValue={inputValue}
				menuIsOpen
				menuShouldScrollIntoView={false}
				tabSelectsValue={false}
				noOptionsMessage={() => intl.formatMessage(messages.noOptionsMessage)}
				onInputChange={onInputChange}
				isOptionDisabled={onIsOptionDisabled as any} // Typescript fix: SelectOption is not compatible with SingleOption from Select
				filterOption={onFilterOption}
				placeholder={placeholder ?? <FormattedMessage {...messages.searchPlaceholder} />}
			/>
		);
	},
);

LocalBaseSelect.displayName = 'LocalBaseSelect';
